import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  Button,
  EmptyState,
  FormField,
  IconButton,
  LoadingState,
  Modal,
  SectionCard,
  SelectInput,
  StatusBadge,
  TextAreaInput,
  TextInput,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import SeletorCompetencia from "./SeletorCompetencia";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { dataCompleta, dataCurta, hojeIso } from "./pontoUtils";

const NOMES_DIA = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const diasDoMes = (competencia) => {
  const [ano, mes] = competencia.split("-").map(Number);
  const total = new Date(ano, mes, 0).getDate();
  return Array.from({ length: total }, (_, indice) => {
    const dia = String(indice + 1).padStart(2, "0");
    const data = `${competencia}-${dia}`;
    return {
      data,
      diaSemana: NOMES_DIA[new Date(ano, mes - 1, indice + 1).getDay()],
    };
  });
};

// Diferença, em minutos, entre o horário digitado e o momento do registro.
const MINUTOS_PARA_DESTACAR = 5;

const paraMinutos = (hora) => {
  const [h, m] = (hora || "0:0").split(":").map(Number);
  return h * 60 + m;
};

const lancadaDepois = (marcacao) =>
  marcacao.horarioRegistrado &&
  marcacao.horarioInformado &&
  paraMinutos(marcacao.horarioRegistrado) -
    paraMinutos(marcacao.horarioInformado) >=
    MINUTOS_PARA_DESTACAR;

const descreverHistorico = (marcacao) => {
  const partes = [];
  if (marcacao.horarioInformado) {
    partes.push(`Digitado: ${marcacao.horarioInformado}`);
  }
  if (marcacao.horarioRegistrado) {
    partes.push(`Registrado às ${marcacao.horarioRegistrado}`);
  }
  if (marcacao.justificativa) {
    partes.push(
      `${marcacao.ajustadoPor || "Gestão"}: ${marcacao.justificativa}`,
    );
  }
  return partes.join(" · ");
};

const FORM_VAZIO = {
  modo: null,
  marcacao: null,
  data: "",
  horario: "",
  justificativa: "",
};

/**
 * Ajustes de marcação pela gestão. Toda inclusão, alteração ou remoção exige justificativa,
 * e o horário original registrado pelo servidor continua guardado.
 */
const MarcacoesTab = ({ colaboradoras, filtro, onFiltro }) => {
  const { showError, showSuccess } = useNotification();
  const [marcacoes, setMarcacoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [mostrarRemovidas, setMostrarRemovidas] = useState(false);

  const usuarioId = filtro.usuarioId || colaboradoras[0]?.usuarioId || "";

  const carregar = useCallback(async () => {
    if (!usuarioId) return;
    setCarregando(true);
    try {
      setMarcacoes(
        await pontoService.listarMarcacoes(usuarioId, filtro.competencia),
      );
    } catch (erro) {
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [usuarioId, filtro.competencia]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const porDia = useMemo(() => {
    const mapa = {};
    marcacoes.forEach((marcacao) => {
      if (!mostrarRemovidas && marcacao.cancelada) return;
      (mapa[marcacao.data] = mapa[marcacao.data] || []).push(marcacao);
    });
    return mapa;
  }, [marcacoes, mostrarRemovidas]);

  const hoje = hojeIso();

  const abrir = (modo, dados) => setForm({ ...FORM_VAZIO, modo, ...dados });
  const fechar = () => setForm(FORM_VAZIO);

  const salvar = async () => {
    setSalvando(true);
    try {
      if (form.modo === "incluir") {
        await pontoService.incluirMarcacao({
          usuarioId,
          data: form.data,
          horario: form.horario,
          justificativa: form.justificativa,
        });
        showSuccess("Marcação incluída.");
      } else if (form.modo === "ajustar") {
        await pontoService.ajustarMarcacao(form.marcacao.id, {
          horario: form.horario,
          justificativa: form.justificativa,
        });
        showSuccess("Marcação ajustada.");
      } else if (form.modo === "remover") {
        await pontoService.cancelarMarcacao(
          form.marcacao.id,
          form.justificativa,
        );
        showSuccess("Marcação removida.");
      }
      fechar();
      await carregar();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setSalvando(false);
    }
  };

  if (!colaboradoras.length) {
    return (
      <SectionCard>
        <EmptyState
          title="Nenhuma colaboradora no ponto."
          description="Cadastre a jornada na aba Colaboradoras."
        />
      </SectionCard>
    );
  }

  const titulos = {
    incluir: "Incluir marcação",
    ajustar: "Ajustar horário",
    remover: "Remover marcação",
  };

  return (
    <div className="space-y-4">
      <SectionCard bodyClassName="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SeletorCompetencia
            competencia={filtro.competencia}
            onChange={(competencia) =>
              onFiltro({ ...filtro, usuarioId, competencia })
            }
          />
          <SelectInput
            aria-label="Colaboradora"
            className="!w-64"
            value={usuarioId}
            onChange={(evento) =>
              onFiltro({ ...filtro, usuarioId: evento.target.value })
            }
          >
            {colaboradoras.map((item) => (
              <option key={item.usuarioId} value={item.usuarioId}>
                {item.nome}
              </option>
            ))}
          </SelectInput>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={mostrarRemovidas}
            onChange={(evento) => setMostrarRemovidas(evento.target.checked)}
          />
          Mostrar marcações removidas
        </label>
      </SectionCard>

      <SectionCard bodyClassName="p-0">
        {carregando ? (
          <LoadingState label="Carregando marcações..." />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Dia</Th>
                <Th>Marcações</Th>
                <Th className="text-right">Incluir</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {diasDoMes(filtro.competencia).map(({ data, diaSemana }) => {
                const doDia = porDia[data] || [];
                const ativas = doDia.filter((item) => !item.cancelada).length;
                const incompleto = ativas % 2 === 1 && data < hoje;
                const futuro = data > hoje;
                return (
                  <tr key={data} className={incompleto ? "bg-amber-50" : ""}>
                    <Td className="tabular-nums">{dataCurta(data)}</Td>
                    <Td>{diaSemana}</Td>
                    <Td className="!whitespace-normal">
                      <div className="flex flex-wrap items-center gap-2">
                        {doDia.map((marcacao) => (
                          <span
                            key={marcacao.id}
                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 ${
                              marcacao.cancelada
                                ? "border-gray-200 bg-gray-50 text-gray-400 line-through"
                                : marcacao.ajustada
                                  ? "border-blue-200 bg-blue-50"
                                  : "border-gray-200 bg-white"
                            }`}
                            title={descreverHistorico(marcacao)}
                          >
                            <span className="font-semibold tabular-nums">
                              {marcacao.horario}
                            </span>
                            {marcacao.ajustada &&
                              !marcacao.cancelada &&
                              (marcacao.horarioInformado ? (
                                <span className="text-xs text-blue-700">
                                  (era {marcacao.horarioInformado})
                                </span>
                              ) : (
                                <span className="text-xs text-blue-700">
                                  (incluída)
                                </span>
                              ))}
                            {!marcacao.ajustada && lancadaDepois(marcacao) && (
                              <span
                                className="text-xs text-gray-500"
                                title="Horário digitado pela colaboradora; este é o momento em que ela registrou."
                              >
                                (lançado {marcacao.horarioRegistrado})
                              </span>
                            )}
                            {!marcacao.cancelada && (
                              <>
                                <IconButton
                                  size="sm"
                                  variant="info"
                                  icon={Pencil}
                                  label={`Ajustar ${marcacao.horario}`}
                                  className="h-6 w-6 p-1"
                                  onClick={() =>
                                    abrir("ajustar", {
                                      marcacao,
                                      data,
                                      horario: marcacao.horario,
                                    })
                                  }
                                />
                                <IconButton
                                  size="sm"
                                  variant="danger"
                                  icon={Trash2}
                                  label={`Remover ${marcacao.horario}`}
                                  className="h-6 w-6 p-1"
                                  onClick={() =>
                                    abrir("remover", { marcacao, data })
                                  }
                                />
                              </>
                            )}
                          </span>
                        ))}
                        {incompleto && (
                          <StatusBadge variant="warning">
                            incompleto
                          </StatusBadge>
                        )}
                      </div>
                    </Td>
                    <Td className="text-right">
                      <IconButton
                        size="sm"
                        icon={Plus}
                        label={`Incluir marcação em ${dataCurta(data)}`}
                        disabled={futuro || ativas >= 4}
                        onClick={() => abrir("incluir", { data })}
                      />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </SectionCard>

      <Modal
        open={Boolean(form.modo)}
        onClose={fechar}
        title={titulos[form.modo] || ""}
        description={form.data ? dataCompleta(form.data) : null}
        className="max-w-md"
      >
        <div className="space-y-4">
          {form.modo === "remover" ? (
            <p className="text-sm text-gray-700">
              A marcação das <strong>{form.marcacao?.horario}</strong> deixará
              de contar no cartão. Ela continua guardada no histórico.
            </p>
          ) : (
            <FormField label="Horário" htmlFor="ponto-horario" required>
              <TextInput
                id="ponto-horario"
                type="time"
                value={form.horario}
                onChange={(evento) =>
                  setForm({ ...form, horario: evento.target.value })
                }
              />
            </FormField>
          )}
          <FormField
            label="Justificativa"
            htmlFor="ponto-justificativa"
            required
            hint="Ex.: esqueceu de registrar a saída; confirmado com a colaboradora."
          >
            <TextAreaInput
              id="ponto-justificativa"
              maxLength={500}
              value={form.justificativa}
              onChange={(evento) =>
                setForm({ ...form, justificativa: evento.target.value })
              }
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={fechar}>
              Cancelar
            </Button>
            <Button
              variant={form.modo === "remover" ? "danger" : "primary"}
              loading={salvando}
              disabled={
                form.justificativa.trim().length < 5 ||
                (form.modo !== "remover" && !form.horario)
              }
              onClick={salvar}
            >
              {form.modo === "remover" ? "Remover" : "Salvar"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MarcacoesTab;
